import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock } from "lucide-react";

const PtcLogoMark = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect width="44" height="44" fill="#d4a853"/>
    <rect x="3" y="3" width="38" height="38" fill="none" stroke="#1a2b4a" strokeWidth="2"/>
    {/* P */}
    <rect x="8"    y="7"  width="2.5" height="13" fill="#1a2b4a"/>
    <rect x="8"    y="7"  width="7"   height="2"  fill="#1a2b4a"/>
    <rect x="12.5" y="7"  width="2.5" height="6.5" fill="#1a2b4a"/>
    <rect x="8"    y="12" width="7"   height="2"  fill="#1a2b4a"/>
    {/* T */}
    <rect x="18" y="7" width="9" height="2"  fill="#1a2b4a"/>
    <rect x="21" y="7" width="3" height="13" fill="#1a2b4a"/>
    {/* C */}
    <rect x="30" y="7"  width="2.5" height="13" fill="#1a2b4a"/>
    <rect x="30" y="7"  width="7"   height="2"  fill="#1a2b4a"/>
    <rect x="30" y="18" width="7"   height="2"  fill="#1a2b4a"/>
    {/* Divider */}
    <rect x="9" y="22" width="26" height="1" fill="#1a2b4a" opacity="0.4"/>
    {/* Broom handle — diagonal parallelogram, upper-right to lower-left */}
    <polygon points="29,23 31,25 21,32 19,30" fill="#1a2b4a"/>
    {/* Broom head — trapezoid, wider at bottom */}
    <polygon points="15,31 27,31 29,36 13,36" fill="#1a2b4a"/>
    {/* Bristles — varying lengths, longest in centre */}
    <rect x="14"   y="36" width="1.2" height="1.5" fill="#1a2b4a"/>
    <rect x="16.5" y="36" width="1.2" height="2"   fill="#1a2b4a"/>
    <rect x="19"   y="36" width="1.2" height="2.5" fill="#1a2b4a"/>
    <rect x="21.5" y="36" width="1.2" height="2.5" fill="#1a2b4a"/>
    <rect x="24"   y="36" width="1.2" height="2"   fill="#1a2b4a"/>
    <rect x="26.5" y="36" width="1.2" height="1.5" fill="#1a2b4a"/>
  </svg>
);

const SERVICE_LINKS = [
  { label: "End of Tenancy Cleaning", path: "/end-of-tenancy-cleaning-london" },
  { label: "Deep Cleaning", path: "/deep-cleaning-london" },
  { label: "Carpet Cleaning", path: "/carpet-cleaning-london" },
  { label: "Oven Cleaning", path: "/oven-cleaning-london" },
];

const COMPANY_LINKS = [
  { label: "Get a Quote", path: "/get-a-quote" },
  { label: "Prices", path: "/prices" },
  { label: "About", path: "/about" },
  { label: "FAQ", path: "/faq" },
  { label: "Contact", path: "/contact" },
];

export const Footer = () => {
  return (
    <footer className="bg-navy-dark py-16" data-testid="site-footer">
      <div className="container mx-auto px-6 md:px-12 lg:px-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          <div>
            <Link to="/" className="flex items-center gap-3 mb-6">
              <PtcLogoMark />
              <div className="flex flex-col leading-none gap-1">
                <span className="font-body font-black text-sm tracking-[0.1em] uppercase text-white">
                  Prime Tenancy
                </span>
                <div className="h-[2px] w-full bg-gold" />
                <span className="font-body font-bold text-[9px] tracking-[0.25em] uppercase text-gold">
                  Cleaning
                </span>
              </div>
            </Link>
            <p className="text-slate-400 mb-6 leading-relaxed">
              London's trusted end of tenancy cleaning service. Serving all 32
              boroughs with a 7-day re-clean guarantee, fully insured
              cleaners and estate-agent approved standards.
            </p>
            <div className="flex gap-3">
              <a
                href="https://wa.me/447346006547"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-teal transition-colors"
                aria-label="WhatsApp"
              >
                <svg
                  className="w-5 h-5 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </a>
              <a
                href="mailto:info@primetenancycleaning.com"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-teal transition-colors"
                aria-label="Email"
              >
                <Mail className="w-5 h-5 text-white" />
              </a>
              <a
                href="tel:+442080363586"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-teal transition-colors"
                aria-label="Phone"
              >
                <Phone className="w-5 h-5 text-white" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-white mb-6">Services</h4>
            <ul className="space-y-3">
              {SERVICE_LINKS.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-teal transition-colors"
                    data-testid={`footer-${link.path}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-white mb-6">Company</h4>
            <ul className="space-y-3">
              {COMPANY_LINKS.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-teal transition-colors"
                    data-testid={`footer-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-white mb-6">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" />
                <div>
                  <a
                    href="tel:+442080363586"
                    className="text-white font-medium hover:text-teal"
                  >
                    020 8036 3586
                  </a>
                  <p className="text-slate-400 text-sm">Mon–Sun 8am–8pm</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" />
                <a
                  href="mailto:info@primetenancycleaning.com"
                  className="text-slate-400 hover:text-teal transition-colors"
                >
                  info@primetenancycleaning.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" />
                <p className="text-slate-400">
                  All 32 London Boroughs
                  <br />
                  Greater London, UK
                </p>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" />
                <p className="text-slate-400">
                  7 days a week
                  <br />
                  Same-day & weekend availability
                </p>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Prime Tenancy Cleaning. All rights
            reserved.
          </p>
          <div className="flex gap-6 text-sm">
            <Link
              to="/contact"
              className="text-slate-500 hover:text-teal transition-colors"
            >
              Contact
            </Link>
            <Link
              to="/faq"
              className="text-slate-500 hover:text-teal transition-colors"
            >
              FAQ
            </Link>
            <Link
              to="/prices"
              className="text-slate-500 hover:text-teal transition-colors"
            >
              Prices
            </Link>
            <Link
              to="/terms"
              className="text-slate-500 hover:text-teal transition-colors"
              data-testid="footer-terms"
            >
              Terms
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
